import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box } from '@mui/material';

/** Grupo de registros seleccionados que no se pueden eliminar, con su motivo. */
export interface BlockedGroup {
    /** Motivo por el que no se eliminarán. Se muestra como encabezado del grupo. */
    reason: string;
    items: string[];
}

interface ConfirmBulkDeleteModalProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    /** Descripción de cada registro que SÍ se va a eliminar. */
    items: string[];
    /** Tipo de registro en singular. Ej: "empleado", "equipo". */
    entityLabel: string;
    /** Tipo de registro en plural. Ej: "empleados", "equipos". */
    entityLabelPlural: string;
    /**
     * Registros seleccionados que quedarán fuera de la eliminación, agrupados por motivo.
     * Se permite seleccionarlos, pero el modal avisa que se omitirán.
     */
    blockedGroups?: BlockedGroup[];
    loading?: boolean;
}

// Cantidad máxima de registros que se listan en el detalle; el resto se resume.
const MAX_PREVIEW = 10;

const ConfirmBulkDeleteModal: React.FC<ConfirmBulkDeleteModalProps> = ({
    open,
    onClose,
    onConfirm,
    items,
    entityLabel,
    entityLabelPlural,
    blockedGroups = [],
    loading = false
}) => {
    const total = items.length;
    const preview = items.slice(0, MAX_PREVIEW);
    const remaining = total - preview.length;

    const groupsWithItems = blockedGroups.filter(group => group.items.length > 0);
    const blockedTotal = groupsWithItems.reduce((acc, group) => acc + group.items.length, 0);

    // Si toda la selección está bloqueada no hay nada que confirmar.
    const nothingToDelete = total === 0;

    return (
        <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 'bold', color: 'error.main' }}>
                {nothingToDelete
                    ? 'No hay registros que se puedan eliminar'
                    : total === 1
                        ? `¿Está seguro que desea eliminar 1 ${entityLabel}?`
                        : `¿Está seguro que desea eliminar ${total} ${entityLabelPlural}?`}
            </DialogTitle>
            <DialogContent dividers>
                {!nothingToDelete && (
                    <>
                        <Typography variant="body2" sx={{ mb: 1.5, color: 'error.main', fontWeight: 'bold' }}>
                            Esta acción no se puede deshacer.
                        </Typography>
                        <Box>
                            {preview.map((item, index) => (
                                <Typography key={index} variant="body2" sx={{ ml: 2, mb: 0.5 }}>
                                    • {item}
                                </Typography>
                            ))}
                            {remaining > 0 && (
                                <Typography variant="body2" sx={{ ml: 2, mt: 0.5, fontStyle: 'italic' }}>
                                    …y {remaining} más.
                                </Typography>
                            )}
                        </Box>
                    </>
                )}

                {/* Seleccionados que quedan fuera. Se listan por motivo para que quede claro
                    por qué se omiten y que el resto sí se elimina. */}
                {blockedTotal > 0 && (
                    <Box sx={{ mt: nothingToDelete ? 0 : 2.5, p: 1.5, borderRadius: 1, backgroundColor: '#fff8e1', border: '1px solid #ffe082' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#8a6d00', mb: 1 }}>
                            {blockedTotal === 1
                                ? `1 ${entityLabel} seleccionado no se eliminará:`
                                : `${blockedTotal} ${entityLabelPlural} seleccionados no se eliminarán:`}
                        </Typography>
                        {groupsWithItems.map((group, groupIndex) => (
                            <Box key={groupIndex} sx={{ mb: 1 }}>
                                <Typography variant="caption" sx={{ color: '#8a6d00', fontWeight: 600 }}>
                                    {group.reason}
                                </Typography>
                                {group.items.slice(0, MAX_PREVIEW).map((item, index) => (
                                    <Typography key={index} variant="body2" sx={{ ml: 2 }}>
                                        • {item}
                                    </Typography>
                                ))}
                                {group.items.length > MAX_PREVIEW && (
                                    <Typography variant="body2" sx={{ ml: 2, fontStyle: 'italic' }}>
                                        …y {group.items.length - MAX_PREVIEW} más.
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary" disabled={loading}>
                    {nothingToDelete ? 'Cerrar' : 'Cancelar'}
                </Button>
                {!nothingToDelete && (
                    <Button onClick={onConfirm} color="error" variant="contained" disabled={loading}>
                        {loading ? 'Eliminando…' : 'Eliminar'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ConfirmBulkDeleteModal;
